import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { URLSearchParams } from "url";

import { Tab } from "@pentabd/ui";

import { ErrorModal, SuccessModal } from "@components";
import { Employee as InventoryEmployee } from "@containers/Inventory/EmployeeDetails/components/Employee";
import ACR from "./Acr";
import APR from "./Apr";
import AttachedFiles from "./AttachedFiles";
import Discipline from "./Discipline";
import DOSSIER from "./Dossier";
import FamilyInfo from "./FamilyInfo";
import GeneralInfo from "./GeneralInfo";
import GpfHistory from "./GpfHistory";
import Leave from "./Leave";
import Postings from "./Postings";
import Qualification from "./Qualification";
import QualificationRewards from "./QualificationRewards";
import Services from "./Services";

import { useUser } from "@hooks/useUser";

import { getAccess } from "@helpers/permissionController";
import FileInquiry from "./FileInquery";

interface IDType {
  id: number | null;
  isProfileDisabled: boolean;
  isEditEmployee: boolean;
  isCreateEmployee: boolean;
}

export const Employee = ({
  id,
  isProfileDisabled,
  isEditEmployee,
  isCreateEmployee,
}: IDType) => {
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState<number>(0);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [errorSubtitle, setErrorSubtitle] = useState<string>(
    "Something went wrong",
  );
  const [searchParams, setSearchParams]: [URLSearchParams, Function] =
    useSearchParams();
  const changeActiveTab = setActiveTab;

  const closeErrorModal = () => setIsErrorModalOpen(false);

  const closeSuccessModal = () => {
    setIsSuccessModalOpen(false);
  };

  const { user } = useUser();
  const { realm_roles: roles, employeeMasterId } = user || {};

  const tabData = [
    {
      label: t("EMPLOYEE.TABS.GENERAL_INFO"),
      component: (
        <GeneralInfo
          id={id}
          setSearchParams={setSearchParams}
          changeActiveTab={changeActiveTab}
          setIsErrorModalOpen={setIsErrorModalOpen}
          setIsSuccessModalOpen={setIsSuccessModalOpen}
          isProfileDisabled={isProfileDisabled || !isEditEmployee}
          setErrorSubtitle={setErrorSubtitle}
        />
      ),
    },
    {
      label: t("EMPLOYEE.TABS.ATTACHED_FILES"),
      component: (
        <AttachedFiles
          id={id}
          changeActiveTab={changeActiveTab}
          isProfileDisabled={isProfileDisabled || !isEditEmployee}
        />
      ),
    },
    {
      label: t("EMPLOYEE.TABS.FAMILY_INFO"),
      component: (
        <FamilyInfo
          id={id}
          changeActiveTab={changeActiveTab}
          setIsErrorModalOpen={setIsErrorModalOpen}
          setIsSuccessModalOpen={setIsSuccessModalOpen}
          isProfileDisabled={isProfileDisabled}
          canEditEmployee={isEditEmployee}
          canCreateEmployee={isCreateEmployee}
        />
      ),
    },
    {
      label: t("EMPLOYEE.TABS.QUALIFICATIONS"),
      component: (
        <Qualification
          id={id}
          changeActiveTab={changeActiveTab}
          setIsSuccessModalOpen={setIsSuccessModalOpen}
          setIsErrorModalOpen={setIsErrorModalOpen}
          isProfileDisabled={isProfileDisabled || !isEditEmployee}
        />
      ),
    },
    {
      label: t("EMPLOYEE.TABS.SERVICES"),
      component: (
        <Services
          id={id}
          changeActiveTab={changeActiveTab}
          setIsSuccessModalOpen={setIsSuccessModalOpen}
          setIsErrorModalOpen={setIsErrorModalOpen}
          isProfileDisabled={isProfileDisabled || !isEditEmployee}
        />
      ),
    },
    {
      label: t("EMPLOYEE.TABS.POSTINGS"),
      component: (
        <Postings
          id={id}
          changeActiveTab={changeActiveTab}
          setIsSuccessModalOpen={setIsSuccessModalOpen}
          setIsErrorModalOpen={setIsErrorModalOpen}
          isProfileDisabled={isProfileDisabled || !isEditEmployee}
        />
      ),
    },
    {
      label: t("EMPLOYEE.TABS.REWARDS"),
      component: (
        <QualificationRewards
          id={id}
          changeActiveTab={changeActiveTab}
          setIsSuccessModalOpen={setIsSuccessModalOpen}
          setIsErrorModalOpen={setIsErrorModalOpen}
          isProfileDisabled={isProfileDisabled || !isEditEmployee}
        />
      ),
    },
    {
      label: t("EMPLOYEE.TABS.DISCIPLINE"),
      component: (
        <Discipline
          id={id}
          setIsErrorModalOpen={setIsErrorModalOpen}
          setIsSuccessModalOpen={setIsSuccessModalOpen}
          isProfileDisabled={isProfileDisabled}
        />
      ),
    },
    {
      ...((getAccess(roles, "ACR_UPDATE_PAGE") ||
        getAccess(roles, "ACR_LIST_PAGE")) &&
        !(Number(id) === Number(employeeMasterId)) && {
          label: t("EMPLOYEE.TABS.ACR"),
          component: (
            <ACR
              id={id}
              changeActiveTab={changeActiveTab}
              setIsSuccessModalOpen={setIsSuccessModalOpen}
              setIsErrorModalOpen={setIsErrorModalOpen}
              isProfileDisabled={isProfileDisabled}
            />
          ),
        }),
    },
    {
      ...(getAccess(roles, "APR_UPDATE") &&
        !(Number(id) === Number(employeeMasterId)) && {
          label: t("EMPLOYEE.TABS.APR"),
          component: (
            <APR
              id={id}
              setIsSuccessModalOpen={setIsSuccessModalOpen}
              setIsErrorModalOpen={setIsErrorModalOpen}
              isProfileDisabled={isProfileDisabled}
            />
          ),
        }),
    },
    {
      ...((getAccess(roles, "DOSSIER_UPDATE") ||
        getAccess(roles, "DOSSIER_LIST_PAGE")) &&
        !(Number(id) === Number(employeeMasterId)) && {
          label: t("EMPLOYEE.TABS.DOSSIER"),
          component: (
            <DOSSIER
              id={id}
              setIsSuccessModalOpen={setIsSuccessModalOpen}
              setIsErrorModalOpen={setIsErrorModalOpen}
              isProfileDisabled={isProfileDisabled}
            />
          ),
        }),
    },
    {
      label: t("EMPLOYEE.TABS.LEAVE"),
      component: (
        <Leave
          id={id}
          isProfileDisabled={isProfileDisabled}
          isEditEmployee={!isEditEmployee}
        />
      ),
    },
    {
      ...(Number(employeeMasterId) === Number(id) && {
        label: t("EMPLOYEE.TABS.GPF"),
        component: <GpfHistory id={id} isEditEmployee={isEditEmployee} />,
      }),
    },
    {
      label: t("EMPLOYEE.TABS.DESK"),
      component: (
        <InventoryEmployee
          id={id}
          isProfile={true}
          isProfileDisabled={isProfileDisabled || !isEditEmployee}
        />
      ),
    },
    {
      label: t("EMPLOYEE.TABS.FILE_INQUIRY"),
      component: (
        <FileInquiry
          id={id}
          setIsErrorModalOpen={setIsErrorModalOpen}
          setIsSuccessModalOpen={setIsSuccessModalOpen}
          isProfileDisabled={isProfileDisabled}
        />
      ),
    },
  ];

  useEffect(() => {
    setActiveTab((prev) => (prev = Number(searchParams.get("tab"))));
  }, [searchParams]);

  useEffect(() => {
    const reset = () => {
      setIsErrorModalOpen(false);
      setIsSuccessModalOpen(false);
    };
    return reset();
  }, []);

  return (
    <>
      <SuccessModal
        isModalOpen={isSuccessModalOpen}
        action={closeSuccessModal}
      />
      <ErrorModal
        isModalOpen={isErrorModalOpen}
        closeModal={closeErrorModal}
        subtitle={errorSubtitle || "Something went wrong"}
      />
      <Tab active={activeTab} setActiveTab={changeActiveTab}>
        <Tab.Heads>
          {tabData.map(({ label }, i) => (
            <Tab.Item
              key={i}
              index={i}
              label={label}
              disabled={i > 0 && !id ? true : false}
            />
          ))}
        </Tab.Heads>
        <Tab.ContentWrapper>
          {tabData.map(({ component }, i) => (
            <Tab.Content key={i} index={i}>
              {component}
            </Tab.Content>
          ))}
        </Tab.ContentWrapper>
      </Tab>
    </>
  );
};
